import { isPlatformBrowser } from '@angular/common';
import { Component, HostBinding, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import {
    ApiService,
    PageService,
    PageType,
    TranslateService,
    WindowService,
    ZoneService,
} from '@gallaghercp/core-frontend';
import { TranslateService as TranslateProvider } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { APP_CONFIG } from '@core-app/app.config';

@Component({
    selector: 'core-footer',
    templateUrl: './footer.component.html',
    standalone: false,
})
export class FooterComponent implements OnInit, OnDestroy {
    private pages = inject(PageService);
    private api = inject(ApiService);
    protected translate = inject(TranslateService);
    protected zoneService = inject(ZoneService);
    protected router = inject(Router);
    protected windowService = inject(WindowService);
    private platformId = inject(PLATFORM_ID);
    isBrowser: boolean = isPlatformBrowser(this.platformId);

    @HostBinding('style.display') displayStyle: string = 'block';
    copyright: string | undefined = APP_CONFIG.copyright;
    year: number = new Date().getFullYear();

    pages$?: Observable<any>;
    subscription: Subscription = new Subscription();
    translateProvider: TranslateProvider = inject(TranslateProvider);
    cookiePolicy?: string = (APP_CONFIG as any).cookiePolicy;
    isFooterBar?: boolean = (APP_CONFIG as any).footerBar ?? true;
    disclaimer?: string = (APP_CONFIG as any).footer?.disclaimer;
    footerStyle?: string = (APP_CONFIG as any).footer?.style ?? 'legacy';
    isSplashLandingConfigEnabled = !!(APP_CONFIG as any).splashLandingPage;
    isSplashLandingCurrentPage: boolean = false;
    isSplashLandingFooterLinks = (APP_CONFIG as any).splashLandingPage
        ? ((APP_CONFIG as any).splashLandingPage['isFooterLinks'] ?? true)
        : true;
    hideFooterLinksOnSplashLanding: boolean = false;
    isZoneRouteOne = (APP_CONFIG as any).splashLandingPage
        ? (APP_CONFIG as any).splashLandingPage['isZoneRouteOne'] !== undefined
            ? (APP_CONFIG as any).splashLandingPage['isZoneRouteOne']
            : true
        : false;
    isFooterButtonBackToSplashPageEnabled = (APP_CONFIG as any).splashLandingPage
        ? (APP_CONFIG as any).splashLandingPage['isFooterButtonBackToSplashPageEnabled']
        : false;
    showFooterButtonBackToSplashPage: boolean = false;

    ngOnInit() {
        this.subscription.add(
            this.api.zoneChangeEmit.subscribe(() => {
                this.getPages();
            }),
        );

        this.subscription.add(
            this.translateProvider.onLangChange.subscribe(() => {
                this.getPages();
            }),
        );

        // Footer logo selection is now handled by <core-brand-logo> contrast logic.

        if (this.isSplashLandingConfigEnabled) {
            // Only get's run if the splashLandingPage object is present in app.config
            this.updateSplashLandingFooterState();
        }

        this.subscription.add(
            this.router.events.subscribe((event: any) => {
                if (event instanceof NavigationEnd) {
                    if (this.isSplashLandingConfigEnabled) {
                        this.updateSplashLandingFooterState();
                    }
                }
            }),
        );
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    // Footer no longer computes a logo URL; BrandLogoComponent selects by contrast.

    private getPages(): void {
        this.pages$ = this.pages.getPages().pipe(
            map((pages: PageType[]) =>
                pages.map((page: PageType) => ({
                    ...page,
                    name: this.translate.getPageSpecific(page, 'name'),
                    description: this.translate.getPageSpecific(page, 'description'),
                    url: this.zoneService.injectZoneCodeUrl(this.translate.getPageSpecific(page, 'url')),
                    tags: this.translate.getPageSpecific(page, 'tags'),
                })),
            ),
        );
    }

    private updateSplashLandingFooterState(): void {
        const isHome =
            this.router.url === '/' || (this.isBrowser && this.windowService.nativeWindow?.location.pathname === '/');
        this.isSplashLandingCurrentPage = isHome && this.isZoneRouteOne;

        if (this.isSplashLandingCurrentPage) {
            this.hideFooterLinksOnSplashLanding = !this.isSplashLandingFooterLinks;
        } else {
            this.hideFooterLinksOnSplashLanding = false;
        }

        this.showFooterButtonBackToSplashPage =
            this.isFooterButtonBackToSplashPageEnabled && !this.isSplashLandingCurrentPage;
    }
}
